import { Component } from '@theme/component';

/**
 * Renders the Wishlist Power list with the theme's product cards.
 * Wishlist Power's public API is plan-gated, so this reads the list through the functions its app embed exposes
 * and falls back to the app's own listing block when they are missing.
 *
 * @typedef {Object} Refs
 * @property {HTMLUListElement} grid - The product grid
 * @property {HTMLElement} empty - The empty state
 * @property {HTMLElement} share - The share links wrapper
 * @property {HTMLElement} shareTitle - Heading above the fallback share links
 * @property {HTMLButtonElement} shareNative - Opens the device's share sheet
 * @property {HTMLAnchorElement} shareSms - Opens the messages app with the list link
 * @property {HTMLAnchorElement} shareWhatsapp - Opens WhatsApp with the list link
 *
 * @extends {Component<Refs>}
 */
class WishlistProducts extends Component {
  requiredRefs = ['grid', 'empty', 'share', 'shareTitle', 'shareNative', 'shareSms', 'shareWhatsapp'];

  #shareURL = '';

  #isShared = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('ooo-wishlist:product-removed', this.#onProductRemoved);
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('ooo-wishlist:product-removed', this.#onProductRemoved);
  }

  get #wishlist() {
    return window.ooo?.wishlist?.functions;
  }

  async #render() {
    const wishlist = await this.#waitForWishlist();

    if (typeof wishlist?.getContent !== 'function') {
      this.setAttribute('fallback', '');
      return;
    }

    try {
      const parameters = new URL(location.href).searchParams;
      const sharedHandles = parameters.get('wishlist_products')?.split(',').filter(Boolean) ?? parameters.getAll('wishlist_product');
      this.#isShared = sharedHandles.length > 0 || (wishlist.isSharedWishlist?.() ?? false);
      const content = sharedHandles.length
        ? { products: sharedHandles.map((handle) => ({ handle })) }
        : await wishlist.getContent(false, this.#isShared);
      const handles = [...new Set((content?.products ?? []).map((product) => product.handle))];
      const cards = await Promise.all(handles.map((handle) => this.#fetchCard(handle)));

      this.refs.grid.replaceChildren(...cards.filter((card) => card !== null));
      this.refs.share.hidden =
        this.#isShared ||
        wishlist.showShareWishlistButton?.() === false ||
        wishlist.isSharedWishlistAccessible?.() === false;
    } catch (error) {
      console.error(error);
      this.setAttribute('fallback', '');
      return;
    }

    this.#updateEmptyState();
    await this.#updateShareLinks();
    this.removeAttribute('loading');
  }

  /**
   * @returns {Promise<any>}
   */
  #waitForWishlist() {
    if (this.#wishlist) return Promise.resolve(this.#wishlist);

    return new Promise((resolve) => {
      const timeout = setTimeout(() => resolve(this.#wishlist), 5000);
      document.addEventListener(
        'ooo-wishlist:ready',
        () => {
          clearTimeout(timeout);
          resolve(this.#wishlist);
        },
        { once: true }
      );
    });
  }

  /**
   * @param {string} handle
   * @returns {Promise<Element | null>}
   */
  async #fetchCard(handle) {
    const url = new URL(`${window.Shopify?.routes?.root ?? '/'}products/${encodeURIComponent(handle)}`, location.origin);
    url.searchParams.set('view', 'wishlist-card');

    const response = await fetch(url);
    if (!response.ok) return null;

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    return html.querySelector('.product-grid__item');
  }

  /**
   * @param {CustomEvent} event
   */
  #onProductRemoved = (event) => {
    if (this.#isShared) return;

    const { handle, product_id: productId } = event.detail?.product ?? {};
    /** @type {HTMLElement[]} */
    const items = [...this.refs.grid.querySelectorAll(':scope > [data-product-id]')];
    const card = items.find((item) => item.dataset.productHandle === handle || item.dataset.productId === String(productId));

    card?.remove();
    this.#updateEmptyState();
    this.#updateShareLinks();
  };

  #updateEmptyState() {
    const isEmpty = this.refs.grid.children.length === 0;

    this.refs.grid.hidden = isEmpty;
    this.refs.empty.hidden = !isEmpty;
    if (isEmpty) this.refs.share.hidden = true;
  }

  /**
   * Real links rather than click handlers: opening sms: or WhatsApp after an await would be blocked as a popup.
   */
  async #updateShareLinks() {
    if (this.refs.share.hidden) return;

    const url = await this.#getShareURL();
    this.#shareURL = url;
    this.#showNativeShare();
    const message = encodeURIComponent((this.dataset.shareMessage ?? '[url]').replace('[url]', url));

    // "?&body=" is understood by both iOS and Android messaging apps
    this.refs.shareSms.href = `sms:?&body=${message}`;
    this.refs.shareWhatsapp.href = `https://wa.me/?text=${message}`;
  }

  /**
   * One button opening the device's share sheet replaces the SMS and WhatsApp links where the browser supports it.
   */
  #showNativeShare() {
    const supported = typeof navigator.share === 'function' && (navigator.canShare?.({ url: this.#shareURL }) ?? true);
    const { shareNative, shareTitle, shareSms, shareWhatsapp } = this.refs;

    shareNative.hidden = !supported;
    shareTitle.hidden = supported;
    shareSms.hidden = supported;
    shareWhatsapp.hidden = supported;
  }

  /**
   * Called straight from the click, with the link already built: the share sheet only opens on a direct user gesture.
   */
  shareNative() {
    if (!this.#shareURL) return;

    const text = (this.dataset.shareMessage ?? '').replace('[url]', '').trim();
    navigator.share({ title: this.dataset.shareTitle, text, url: this.#shareURL }).catch((error) => {
      if (error.name !== 'AbortError') console.error(error);
    });
  }

  /**
   * @returns {Promise<string>}
   */
  async #getShareURL() {
    const { customerSharedId, customerHmac } = window.ooo?.wishlist ?? {};
    let url;

    if (customerSharedId && customerSharedId !== '-' && customerHmac) {
      url = await this.#wishlist?.getSharedWishlistURL?.();
    }

    if (!url) {
      // Guests have no native sharing ID, so share the current selection of product handles.
      const sharedURL = new URL(location.href);
      for (const parameter of ['id', 'hmac', 'customer_name', 'wishlist_product', 'wishlist_products']) {
        sharedURL.searchParams.delete(parameter);
      }
      sharedURL.hash = '';

      const handles = [...this.refs.grid.children].map((card) => card.getAttribute('data-product-handle')).filter(Boolean);
      sharedURL.searchParams.set('wishlist_products', handles.join(','));

      url = sharedURL.href;
    }

    const sharedURL = new URL(url, location.origin);
    const theme = window.Shopify?.theme;
    if (theme?.id && theme.role && theme.role !== 'main') {
      sharedURL.searchParams.set('preview_theme_id', String(theme.id));
    }

    return sharedURL.href;
  }
}

if (!customElements.get('wishlist-products')) {
  customElements.define('wishlist-products', WishlistProducts);
}
